import { css, jsx } from '@emotion/core';
import { Select, Skeleton } from 'antd';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import Form from '@/src/shared/components/Form';
import formatURL from '@/utils/formatURL';
import searchContent from '@/utils/searchContent';
import states from '@/utils/states';

import ListPage from './index';

const style = {
  noPrint: css`
    @media print {
      display: none;
    }
  `,
  select: css`
    max-width: 50%;
    margin-bottom: 4px;
  `,
};

const ListPageStates = props => {
  const {
    statecode,
    results,
    title,
    loading,
    searchOptions,
    routeBase,
    titlePrefix,
    includeAll,
    onSelectReplace,
    ..._props
  } = props;
  const router = useRouter();
  const { query } = router;
  const [_results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [current, setCurrent] = useState(Number(query.page) || 1);
  const [selectedState, setSelectedState] = useState(statecode ? statecode.toUpperCase() : 0);

  useEffect(() => {
    setResults(results);
  }, [results]);

  useEffect(() => {
    setCurrent(query.page ? Number(query.page) : 1);
  }, [query.page]);

  function handlePaginationChange(page) {
    setCurrent(page);
    const newURL = formatURL(router.asPath, { page });
    router.push(newURL, newURL, { shallow: false });
  }

  async function handleSelectChange(value) {
    setLoadingResults(true);
    setCurrent(1);
    try {
      const selectedAllStates = value === 0;
      const response = await searchContent(
        {
          ...searchOptions,
          ...(!selectedAllStates && { stateCode: value }),
        },
        { triggerLoad: false }
      );
      setResults(response.results);

      if (onSelectReplace === 'route') {
        router.push(
          `${selectedAllStates ? routeBase : `${routeBase}/[slug]`}`,
          formatURL(`${routeBase}${selectedAllStates ? '' : `/${value.toUpperCase()}`}`, {
            page: null,
          }),
          { shallow: true }
        );
      }
      if (onSelectReplace === 'query') {
        const newURL = formatURL(router.asPath, { stateCode: value.toUpperCase() });
        router.push(newURL, newURL, { shallow: true });
      }

      setTimeout(() => {
        setLoadingResults(false);
        setSelectedState(value);
      }, 100);
    } catch (error) {
      setResults(results);
      setTimeout(() => setLoadingResults(false), 100);
    }
  }

  const _loading = loading || loadingResults;
  // eslint-disable-next-line react/destructuring-assignment
  const _states = props.states || states;

  return (
    <ListPage
      current={current}
      handlePaginationChange={handlePaginationChange}
      pagination
      loading={_loading}
      results={_results}
      statecode={statecode}
      title={
        _loading ? (
          <Skeleton title={{ width: '50%' }} paragraph={false} active />
        ) : (
          `${
            selectedState
              ? `${_states.find(s => s.StateCode === selectedState).StateDescription}`
              : titlePrefix
          } ${title}`
        )
      }
      renderBeforeList={() => (
        <Form theme="white" css={style.noPrint}>
          <Form.Item label="Display for" colon>
            <Select
              defaultValue={statecode}
              onChange={handleSelectChange}
              css={style.select}
              optionFilterProp="children"
              showSearch
            >
              {includeAll ? <Select.Option value={0}>All States</Select.Option> : null}
              {_states.map(state => (
                <Select.Option key={state.StateCode} value={state.StateCode}>
                  {state.StateDescription}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      )}
      {..._props}
    />
  );
};

ListPageStates.propTypes = {
  heroContent: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  title: PropTypes.string.isRequired,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageSize: PropTypes.number,
  renderResultSubtitle: PropTypes.func,
  truncateResultSummary: PropTypes.bool,
  pagination: PropTypes.bool,
  searchOptions: PropTypes.object.isRequired,
  routeBase: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  titlePrefix: PropTypes.string,
  includeAll: PropTypes.bool,
  onSelectReplace: PropTypes.oneOf(['query', 'route']),
};

ListPageStates.defaultProps = {
  heroContent: null,
  pageSize: 20,
  renderResultSubtitle: result => new Date(result.PublishDate).toLocaleDateString(),
  truncateResultSummary: false,
  pagination: true,
  loading: false,
  titlePrefix: 'State',
  includeAll: true,
  onSelectReplace: 'route',
};

export default ListPageStates;
